import axios from "axios";
import { Bell, Download, Plus, Star, TrendingUp } from "lucide-react";
import Image from "next/image";
import ListModal from "src/components/dashboard/listModal";
import { useContext, useState } from "react";
import { SearchContext } from "src/common/search";
import FreeTrialRestrictionModal from "src/common/freeTrialRestrictionModal";
import { StockTableData, Watchlist } from "src/helpers";
import { WatchlistData } from "./WatchlistTable";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import WatchlistStatsSkeleton from "src/components/skeleton/WatchlistStatsSkeleton";
import DialogModal from "src/common/DialogModal";

interface PortfolioStatsCards {
  records: WatchlistData[];
  getData: () => Promise<void>;
  watchlistStatus: IWatchlistStatus;
  positiveTrendCount: number;
  alertTriggeredCount: number;
  selectedRange: string;
  totalItems: number;
  isTrial?: boolean;
  isLoading?: boolean;
}

export interface IWatchlistStatus {
  totalStockCount: number;
  thisWeekStockCount: number;
  alertTriggered: number;
  positiveTrendCount: number;
}

const WatchlistStatsCards = (props: PortfolioStatsCards) => {
  const {
    watchlistStatus,
    getData,
    positiveTrendCount,
    alertTriggeredCount,
    records,
    selectedRange,
    totalItems: _totalItems,
    isTrial,
    isLoading,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const user = getLocalStorageUser();
  const selectedStock: StockTableData | null = null;
  const [watchlist, setWatchlist] = useState<Watchlist>({});
  const [notes, setNotes] = useState("");
  const [freeTrialRestrictModal, setFreeTrialRestrictModal] = useState(false);
  const searchContext = useContext(SearchContext);
  const { setIsLoading } = searchContext;

  // const calculateTodayPositiveStock = useCallback(() => {
  //   let positive = 0;

  //   records.forEach((record) => {
  //     const diff = Number(record.ltp) - Number(record.close);
  //     if (diff > 0) {
  //       positive += 1;
  //     }
  //   });

  //   return positive;
  // }, [records]);

  const getCountValueClass = (count: number) =>
    count > 0 ? "text-black dark:text-[#14D83E]" : "text-black dark:text-white";

  const getCountIconClass = (count: number) =>
    count > 0
      ? "w-4 h-4 text-[#00A85F] dark:text-[#14D83E]"
      : "w-4 h-4 text-black dark:text-white";

  const stats = [
    {
      title: "Total Stocks",
      value: `${watchlistStatus.totalStockCount}`,
      icon: Star,
      isImage: false,
      subText: `+${watchlistStatus.thisWeekStockCount} this week`,
      subTextClass: "text-blue-600 dark:text-blue-400",
      valueClass: "text-black dark:text-[#14D83E]",
      iconClass: "w-4 h-4 text-[#00A85F] dark:text-[#14D83E]",
    },
    {
      title: "Alerts Triggered",
      value: `${alertTriggeredCount}`,
      icon: Bell,
      isImage: false,
      // subText: "0 buy signals",
      subTextClass: "text-green-600 dark:text-green-400",
      valueClass: getCountValueClass(alertTriggeredCount),
      iconClass: getCountIconClass(alertTriggeredCount),
    },
    {
      title: "Positive Trend",
      value: `${positiveTrendCount}`,
      icon: TrendingUp,
      isImage: false,
      subText: `${positiveTrendCount} of ${watchlistStatus.totalStockCount} stocks`,
      subTextClass:
        positiveTrendCount > 0
          ? "text-teal-600 dark:text-teal-400"
          : "text-black dark:text-white",
      valueClass: getCountValueClass(positiveTrendCount),
      iconClass: getCountIconClass(positiveTrendCount),
    },
    // {
    //   title: "Avg. Target Value",
    //   value: "₹2.4L",
    //   icon: Info,
    //   isImage: false,
    //   subText: "Per stock basis",
    //   subTextClass: "text-purple-600 dark:text-purple-400",
    // },
  ];

  const exportWatchlistToCSV = async () => {
    try {
      setIsLoading(true);
      const user = getLocalStorageUser() || {};
      if (!user?.token) {
        console.error("No user token found in localStorage");
        return;
      }

      const params = new URLSearchParams({
        filter: selectedRange,
      });

      const response = await axios.get(
        `/api/users/watchlists/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "watchlist-export.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting portfolio:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetField = () => {
    setNotes("");
    setWatchlist({});
  };

  const watchlistCount = Number(user?.watchlist_count ?? 0);
  const stockLimit = Number(process.env.NEXT_PUBLIC_STOCK_COUNT_LIMIT);
  const isLimitReached = isTrial && watchlistCount >= stockLimit;

  if (isLoading) {
    return <WatchlistStatsSkeleton />;
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="block lg:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="flex items-start gap-2 space-x-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700/50"
            >
              <div className="p-[5px] rounded-full bg-[#baf3d2] dark:bg-[#394564] flex-shrink-0">
                {stat.isImage ? (
                  <Image
                    src={"icon"}
                    alt={stat.title}
                    width={16}
                    height={16}
                    className="w-4 h-4 object-contain filter invert dark:invert-0"
                  />
                ) : (
                  <stat.icon className={stat.iconClass} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-5 font-medium text-[#3D3D3D] dark:text-white font-figtree truncate">
                  {stat.title}
                </p>
                <p
                  className={`text-xl font-medium font-figtree ${stat.valueClass}`}
                >
                  {stat.value}
                </p>
                {stat.subText && (
                  <p
                    className={`text-xs mt-1 font-figtree ${stat.subTextClass}`}
                  >
                    {stat.subText}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Header Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 items-center max-[1024px]:mb-0 max-[1024px]:mx-auto gap-4 !mb-0">
        <div className="flex flex-col items-start pl-4 space-x-2 mb-0 lg:mb-0">
          <h2 className="text-2xl font-medium text-black dark:text-white font-figtree">
            Watchlist Analysis
          </h2>
          <h6 className="text-sm !ml-0 font-medium text-black dark:text-[#a1a1a1] font-figtree">
            Monitor your tracked stocks and price alerts
          </h6>
        </div>

        {/* Controls */}
        <div className="flex sm:flex-row items-start sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 flex-wrap max-sm:gap-1 max-sm:justify-end">
          <button
            className={`
              cursor-pointer flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm transition-all duration-200 font-figtree !mt-0 max-sm:px-2
              ${
                isLimitReached
                  ? "bg-gray-400 cursor-not-allowed text-white opacity-60"
                  : "text-white bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] hover:opacity-90"
              }
            `}
            onClick={() => {
              if (isLimitReached) {
                setFreeTrialRestrictModal(true);
                return;
              }

              setIsModalOpen(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Add Stock
          </button>

          <button
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm text-white transition-all duration-200 font-figtree !mt-0 
    ${
      records.length === 0
        ? "bg-gray-400 cursor-not-allowed opacity-70"
        : "bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] hover:opacity-90 cursor-pointer"
    }`}
            onClick={exportWatchlistToCSV}
            disabled={records.length === 0}
          >
            <Download className="w-4 h-4" />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden flex justify-between items-center border border-[#d0d4e4] dark:border-[#394564] rounded-md bg-white my-0 !mt-2 dark:bg-[#121419] lg:flex w-full shadow-[5px_1px_5px_rgba(0,0,0,0.09)] mb-2">
        <div className="flex items-stretch max-w-max py-2 [@media(min-width:1024px)_and_(max-width:1367px)]:flex-wrap">
          {stats.map((stat, index) => {
            const isFirst = index === 0;
            const isLast = index === stats.length - 1;
            return (
              <div
                key={index}
                className={`flex items-start space-x-3 flex-1 ${isFirst ? "px-4" : "px-6"} py-2 ${!isFirst ? "min-w-[280px]" : ""} ${isFirst ? "max-w-[320px]" : ""} ${!isLast ? "border-r border-[#d0d4e4] dark:border-gray-700" : ""}
                [@media(min-width:1024px)_and_(max-width:1367px)]:min-w-max
                [@media(min-width:1024px)_and_(max-width:1367px)]:px-4`}
              >
                <div className="p-[5px] rounded-full bg-[#baf3d2] dark:bg-[#394564] flex-shrink-0">
                  {stat.isImage ? (
                    <Image
                      src={"icon"}
                      alt={stat.title}
                      width={16}
                      height={16}
                      className="w-4 h-4 object-contain filter invert dark:invert-0"
                    />
                  ) : (
                    <stat.icon className="w-4 h-4 text-black dark:text-white" />
                  )}
                </div>
                <div>
                  <p className="text-sm leading-5 font-medium text-[#3D3D3D] dark:text-white min-w-[140px] font-figtree whitespace-nowrap">
                    {stat.title}
                  </p>
                  <p
                    className={`text-xl font-medium font-figtree leading-tight ${stat.valueClass ?? "text-black dark:text-[#14D83E]"}`}
                  >
                    {stat.value}
                  </p>
                  {stat.subText && (
                    <p
                      className={`text-xs mt-1 font-figtree ${stat.subTextClass}`}
                    >
                      {stat.subText}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {isModalOpen && (
        <DialogModal
          title=""
          message={
            <ListModal
              heading="Add to Watchlist"
              isWatchlist={true}
              StatsCard={true}
              notes={notes}
              selectedStock={selectedStock}
              setNotes={setNotes}
              handleOnClick={() => {
                setIsModalOpen(false);
                handleResetField();
              }}
              watchlist={watchlist}
              setWatchlist={setWatchlist}
              getData={getData}
            />
          }
          isModalList={true}
          show={isModalOpen}
          setShow={setIsModalOpen}
          handleOnClick={() => {
            setIsModalOpen(false);
            handleResetField();
          }}
          noBackdrop={true}
        />
      )}

      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModal}
        onClose={() => setFreeTrialRestrictModal(false)}
        variant="addStock"
      />
    </>
  );
};

export default WatchlistStatsCards;
